import api, { fetchCsrfToken, clearClientAuthState } from './api';

export const userService = {
  /** Garante CSRF em memória a partir do JSON de GET /auth/csrf/. */
  ensureCsrf: async () => fetchCsrfToken(),


  registerUser: async (finalObject) => {
    try {
      await userService.ensureCsrf();
      const response = await api.post('/signup/', finalObject);
      return response.data;
    } catch (error) {
      console.error('Erro na API:', error.response?.data);
      throw error;
    }
  },

  /**
   * Cadastro simplificado PROP/GUARDIAN (ENG/ADM).
   * POST /api/users/create-field/
   * @param {object} payload
   * @param {string} payload.full_name
   * @param {string} payload.email
   * @param {"PROP"|"GUARDIAN"} payload.user_type
   * @param {Array<{id_code: string, role: "TEAM"|"MANAGER"}>} [payload.areas]
   *        TEAM = Membro (other_responsibles); MANAGER = Gestor (adi_responsible)
   */
  createFieldUser: async ({ full_name, email, user_type, areas = [] }) => {
    await userService.ensureCsrf();
    const body = { full_name, email, user_type };
    if (Array.isArray(areas) && areas.length > 0) {
      body.areas = areas.map((a) => ({
        id_code: a.id_code,
        role: a.role === "MANAGER" ? "MANAGER" : "TEAM",
      }));
    }
    const response = await api.post('/users/create-field/', body);
    return response.data;
  },

  /**
   * Login web cookie-only.
   * Tokens ficam só em cookies HttpOnly — o body NÃO traz access/refresh.
   */
  loginUser: async (email, password) => {
    try {
      await userService.ensureCsrf();
      const response = await api.post('/auth/web/login/', { email, password });
      return response.data;
    } catch (error) {
      if (error.response) {
        console.error('❌ Erro no login:', error.response.data?.error || error.response.data);
        const apiError = new Error(
          error.response.data?.error || 'Não foi possível fazer login.'
        );
        apiError.response = error.response;
        throw apiError;
      }
      console.error('❌ Erro inesperado:', error);
      throw new Error('Erro inesperado. Tente novamente.');
    }
  },

  /** Sessão atual via cookie HttpOnly. */
  getMe: async () => {
    const response = await api.get('/auth/me/');
    return response.data;
  },

  logout: async () => {
    try {
      await userService.ensureCsrf();
      await api.post('/auth/logout/', {});
    } catch (error) {
      console.error('Erro no logout (best effort):', error);
    } finally {
      clearClientAuthState();
    }
  },

  resetPassword: async (email) => {
    const response = await api.post('/password-reset/', { email });
    return response.data;
  },

  getUsers: async () => {
    const response = await api.get('/users/');
    return response.data;
  },

  getUserById: async (userId) => {
    try {
      const response = await api.get(`/users/${userId}/`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  getUsersWithoutProjects: async () => {
    try {
      const response = await api.get('/users_without_projects/');
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  updateUser: async (userId, userUpdate) => {
    try {
      const response = await api.put(`/users/${userId}/update/`, userUpdate);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  confirmPasswordReset: async (code, hash, password, password2) => {
    const response = await api.post(`/password-reset-confirm/${code}/${hash}/`, {
      password,
      password2,
    });
    return response.data;
  },

  updatePassword: async (userId, currentPassword, newPassword) => {
    try {
      const response = await api.put(`/reset-password/${userId}/`, {
        current_password: currentPassword,
        new_password: newPassword,
      });

      return response.data;
    } catch (error) {
      console.error('❌ Erro ao alterar senha do usuário:', error);
      throw error;
    }
  },

  resetPasswordByEmail: async (email, currentPassword, newPassword) => {
    try {
      const response = await api.post('/reset-password-by-email/', {
        email,
        current_password: currentPassword,
        new_password: newPassword,
      });
      return response.data;
    } catch (error) {
      console.error('Erro ao alterar senha:', error);
      throw error;
    }
  },

  checkEmailExists: async (email) => {
    try {
      const response = await api.get(
        `users/check-email/?email=${encodeURIComponent(email)}`
      );
      return response.data;
    } catch (error) {
      console.error('❌ Erro ao verificar email:', error);
      throw error;
    }
  },

  /** Preferência light/dark — string "light" | "dark". */
  getTheme: async () => {
    const response = await api.get('/user-settings/theme/');
    return response.data;
  },

  updateTheme: async (theme) => {
    const response = await api.patch('/user-settings/theme/', { theme });
    return response.data;
  },

  /** Preferência de e-mails FIRMS — boolean (default false / opt-in). */
  getFirmsEmailPreference: async () => {
    const response = await api.get('/user-settings/firms-emails/');
    return response.data;
  },

  updateFirmsEmailPreference: async (receive_firms_emails) => {
    const response = await api.patch('/user-settings/firms-emails/', {
      receive_firms_emails: Boolean(receive_firms_emails),
    });
    return response.data;
  },

  /**
   * Login Google legado: ainda pode devolver access/refresh no JSON.
   * Não misturar Bearer com sessão cookie na mesma sessão.
   */
  loginWithGoogle: async (payload) => {
    await userService.ensureCsrf();
    const response = await api.post('/auth/google/', payload);
    return response.data;
  },

  /**
   * Login Google na plataforma web (cookies HttpOnly).
   * Só autentica conta já existente — não cria solicitação/cadastro.
   * POST /api/auth/web/google/
   */
  loginWithGoogleWeb: async ({ email, full_name, google_id, photo_url }) => {
    await userService.ensureCsrf();
    const response = await api.post('/auth/web/google/', {
      email,
      full_name,
      google_id,
      ...(photo_url ? { photo_url } : {}),
    });
    return response.data;
  },
};
